import type { AiCostBreakdownFetchedAction } from '@Application/AiCosts/Actions/aiCostBreakdownFetched';
import type { AiCostCallsFetchedAction } from '@Application/AiCosts/Actions/aiCostCallsFetched';
import type { AiCostFilterOptionsFetchedAction } from '@Application/AiCosts/Actions/aiCostFilterOptionsFetched';
import type { AiCostSummaryFetchedAction } from '@Application/AiCosts/Actions/aiCostSummaryFetched';
import type { AiCostDimension } from '@Application/AiCosts/AiCostDimension';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import AiCostBreakdownListView from '@UI/Store/AiCosts/Views/AiCostBreakdownListView';
import AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';
import AiCostCallView from '@UI/Store/AiCosts/Views/AiCostCallView';
import AiCostCallsListView from '@UI/Store/AiCosts/Views/AiCostCallsListView';
import AiCostFacilityOptionView from '@UI/Store/AiCosts/Views/AiCostFacilityOptionView';
import AiCostFilterOptionsView from '@UI/Store/AiCosts/Views/AiCostFilterOptionsView';
import AiCostOrganizationOptionView from '@UI/Store/AiCosts/Views/AiCostOrganizationOptionView';
import AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import AiCostUserOptionView from '@UI/Store/AiCosts/Views/AiCostUserOptionView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type AiCostsStore = {
	filterOptions: AiCostFilterOptionsView | null;
	summaries: Readonly<Record<string, AiCostSummaryView>>;
	breakdowns: Readonly<Record<AiCostDimension, AiCostBreakdownListView | null>>;
	calls: AiCostCallsListView | null;
};

const initialState: AiCostsStore = {
	filterOptions: null,
	summaries: {},
	breakdowns: {
		facilities: null,
		organizations: null,
		users: null,
		documents: null,
		interviews: null,
		steps: null,
	},
	calls: null,
};

type Actions =
	| AiCostFilterOptionsFetchedAction
	| AiCostSummaryFetchedAction
	| AiCostBreakdownFetchedAction
	| AiCostCallsFetchedAction
	| UserRevokedAction;

type LoadedList = { readonly accountId: number; readonly queryKey: string; readonly loadedPage: number };

/** A page after the first only extends the list it continues, and only once: anything else is stale. */
const continuesList = (
	current: LoadedList | null,
	action: { accountId: number; queryKey: string; page: number },
): current is LoadedList => {
	return (
		current !== null &&
		current.accountId === action.accountId &&
		current.queryKey === action.queryKey &&
		action.page === current.loadedPage + 1
	);
};

const whenAiCostFilterOptionsFetched = (
	store: AiCostsStore,
	action: AiCostFilterOptionsFetchedAction,
): AiCostsStore => ({
	...store,
	filterOptions: new AiCostFilterOptionsView(
		action.accountId,
		action.payload.organizations.map((organization) => new AiCostOrganizationOptionView(organization)),
		action.payload.facilities.map((facility) => new AiCostFacilityOptionView(facility)),
		action.payload.users.map((user) => new AiCostUserOptionView(user)),
	),
});

const whenAiCostSummaryFetched = (store: AiCostsStore, action: AiCostSummaryFetchedAction): AiCostsStore => ({
	...store,
	summaries: {
		...store.summaries,
		[action.queryKey]: new AiCostSummaryView(action.payload, action.accountId, action.queryKey),
	},
});

const whenAiCostBreakdownFetched = (
	store: AiCostsStore,
	action: AiCostBreakdownFetchedAction,
): AiCostsStore => {
	const rows = action.payload.results.map((row) => new AiCostBreakdownRowView(row));
	const current = store.breakdowns[action.dimension];
	let next: AiCostBreakdownListView | null = null;

	if (action.page === 0) {
		next = new AiCostBreakdownListView(
			rows,
			action.dimension,
			action.accountId,
			action.queryKey,
			action.payload.totalRecords,
			0,
		);
	} else if (current !== null && continuesList(current, action)) {
		next = current.append(rows, action.page);
	}

	if (next === null) {
		return store;
	}

	return { ...store, breakdowns: { ...store.breakdowns, [action.dimension]: next } };
};

const whenAiCostCallsFetched = (store: AiCostsStore, action: AiCostCallsFetchedAction): AiCostsStore => {
	const rows = action.payload.results.map((call) => new AiCostCallView(call));

	if (action.page === 0) {
		return {
			...store,
			calls: new AiCostCallsListView(
				rows,
				action.accountId,
				action.queryKey,
				action.payload.totalRecords,
				0,
			),
		};
	}

	if (store.calls !== null && continuesList(store.calls, action)) {
		return { ...store, calls: store.calls.append(rows, action.page) };
	}

	return store;
};

// Costs must not outlive the session that was allowed to read them.
const whenUserRevoked = (): AiCostsStore => initialState;

export default registerStore<'aiCostsStore', Actions>(
	'aiCostsStore',
	createSliceReducer({
		AI_COST_FILTER_OPTIONS_FETCHED: whenAiCostFilterOptionsFetched,
		AI_COST_SUMMARY_FETCHED: whenAiCostSummaryFetched,
		AI_COST_BREAKDOWN_FETCHED: whenAiCostBreakdownFetched,
		AI_COST_CALLS_FETCHED: whenAiCostCallsFetched,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
